import { useQuery } from '@tanstack/react-query';

import {
  ensureClientPreferenceOptions,
  getAllergyOptions,
  getCuisineTypeOptions,
  getDietaryPreferenceOptions,
  getFoodIntoleranceOptions,
  getCachedClientPreferenceOptions,
  type ClientPreferenceOptions,
} from '@/lib/clientPreferenceOptions';

export const CLIENT_PREFERENCE_OPTIONS_QUERY_KEY = ['clients', 'preference-options'] as const;

/**
 * Loads GET /clients/preference-options once (memory + localStorage cache).
 * Subsequent mounts reuse cache without refetching until TTL expires / force refresh.
 */
export function useClientPreferenceOptions(options?: { enabled?: boolean; force?: boolean }) {
  const enabled = options?.enabled ?? true;
  const initialData = getCachedClientPreferenceOptions() ?? undefined;

  const query = useQuery({
    queryKey: CLIENT_PREFERENCE_OPTIONS_QUERY_KEY,
    queryFn: () => ensureClientPreferenceOptions({ force: options?.force }),
    enabled,
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    initialData,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
  });

  const data: ClientPreferenceOptions | undefined = query.data ?? initialData;

  return {
    ...query,
    allergies: data?.allergies ?? getAllergyOptions(),
    foodIntolerances: data?.foodIntolerances ?? getFoodIntoleranceOptions(),
    dietaryPreferences: data?.dietaryPreferences ?? getDietaryPreferenceOptions(),
    cuisineTypes: data?.cuisineTypes ?? getCuisineTypeOptions(),
  };
}
